import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaPlay } from "react-icons/fa";
import { getDownloadURL, getStorage, ref } from "firebase/storage";
import EyeDonationHeader from "./EyeDonationHeader";
import { useTranslation } from "react-i18next";

const Arrow = ({ onClick, direction, className, style, color = "white" }) => {
  const { t } = useTranslation();

  const base = {
    ...style,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.12)",
    boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
    color: color,
    cursor: "pointer",
    zIndex: 4,
  };
  const svgStyle = { width: 18, height: 18, display: "block" };

  return (
    <div
      role="button"
      aria-label={
        direction === "left"
          ? t("home.eyeDonationSection.previousSlide")
          : t("home.eyeDonationSection.nextSlide")
      }
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick?.()}
      tabIndex={0}
      className={className}
      style={base}
    >
      {direction === "left" ? (
        <svg
          viewBox="0 0 24 24"
          style={svgStyle}
          fill="none"
          stroke={color}
          strokeWidth="2"
        >
          <path
            d="M15 18l-6-6 6-6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : (
        <svg
          viewBox="0 0 24 24"
          style={svgStyle}
          fill="none"
          stroke={color}
          strokeWidth="2"
        >
          <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  );
};

const EyeDonationSlider = ({ mediaUrls = {} }) => {
  const [thumbnails, setThumbnails] = useState([null, null]);
  useEffect(() => {
    const storage = getStorage();
    const paths = [
      "home/eyeDonation/video1.PNG",
      "home/eyeDonation/video2.png",
    ];
    Promise.all(
      paths.map((p) => getDownloadURL(ref(storage, p)).catch(() => null))
    ).then((urls) => setThumbnails(urls));
  }, []);
  const { t } = useTranslation();
  const slides = [
    {
      key: "image",
      type: "image",
      src: mediaUrls?.eyeDonationDoctor || "/assets/img/placeholder-doctor.jpg",
      title: t("home.eyeDonationSection.hospitalContacts"),
      heading: t("home.eyeDonationSection.supportEyeDonation"),
      text: t("home.eyeDonationSection.cornealBlindnessInfo"),
      bottomLine: t("home.eyeDonationSection.clickOnImage"),
      bullets: [
        t("home.eyeDonationSection.respectfulHandling"),
        t("home.eyeDonationSection.restoreSight"),
        t("home.eyeDonationSection.registerAndCoordinate"),
      ],
      layout: "media-left",
    },
    {
      key: "gujarati",
      type: "video",
      src:
        mediaUrls?.eyeDonationGujarati || "/assets/video/gujarati-sample.mp4",
      title: t("home.eyeDonationSection.gujaratiAwarenessMessage"),
      heading: t("home.eyeDonationSection.whyCornealBlindness"),
      text: t("home.eyeDonationSection.cornealBlindnessCauses"),
      bullets: [
        t("home.eyeDonationSection.commonCauses"),
        t("home.eyeDonationSection.eyeBanksSupport"),
        t("home.eyeDonationSection.eyesCollectedYearly"),
      ],
      layout: "media-right",
    },
    {
      key: "hindi",
      type: "video",
      src: mediaUrls?.eyeDonationHindi || "/assets/video/hindi-sample.mp4",
      title: t("home.eyeDonationSection.hindiAwarenessMessage"),
      heading: t("home.eyeDonationSection.howDonationRestores"),
      text: t("home.eyeDonationSection.cornealSurgeryInfo"),
      bullets: [
        t("home.eyeDonationSection.advancedTechniques"),
        t("home.eyeDonationSection.youngerDonors"),
        t("home.eyeDonationSection.quickRegistration"),
      ],
      layout: "media-left",
    },
  ];

  const videoRefs = useRef([]);
  videoRefs.current = slides.map(
    (_, i) => videoRefs.current[i] ?? React.createRef()
  );

  const [playingIndex, setPlayingIndex] = useState(null);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayImageSrc, setOverlayImageSrc] = useState(null);
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [slideIndex, setSlideIndex] = useState(0);
  const [isVertical, setIsVertical] = useState(slides.map(() => false));

  const [theme, setTheme] = useState("base");
  // const isAlternate = theme === "alternate";

  const rootRef = useRef(null);
  const radial1Ref = useRef(null);
  const radial2Ref = useRef(null);
  const radial3Ref = useRef(null);
  const radial4Ref = useRef(null);
  const sliderRef = useRef(null);
  const trackRef = useRef(null);

  const slideWidthRef = useRef(null);
  const isSeekingRef = useRef(false);

  const [isInteracting, setIsInteracting] = useState(false);
  const interactionTimeoutRef = useRef(null);

  const smoothedRef = useRef({
    angle: null,
    whiteOpacity: null,
    darkBColor: null,
    p1x: null,
    p1y: null,
    p2x: null,
    p2y: null,
    p3x: null,
    p3y: null,
  });

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
      measureSlideWidth();
    };

    let resizeTimer = null;
    const debounced = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        handleResize();
        resizeTimer = null;
      }, 120);
    };

    window.addEventListener("resize", debounced);
    setTimeout(measureSlideWidth, 80);

    return () => {
      window.removeEventListener("resize", debounced);
      if (resizeTimer) clearTimeout(resizeTimer);
    };
  }, []);

  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef(null);
  const dragThresholdRef = useRef(5);

  useEffect(() => {
    const listEl =
      sliderRef.current?.innerSlider?.list ??
      rootRef.current?.querySelector?.(".slick-list") ??
      rootRef.current;

    if (!listEl) return;

    const onDown = (e) => {
      const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
      const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;

      dragStartRef.current = { x: clientX, y: clientY, time: Date.now() };
      setIsDragging(false);
    };

    const onMove = (e) => {
      if (!dragStartRef.current) return;

      const clientX = e.clientX || (e.touches && e.touches[0]?.clientX) || 0;
      const clientY = e.clientY || (e.touches && e.touches[0]?.clientY) || 0;

      const deltaX = Math.abs(clientX - dragStartRef.current.x);
      const deltaY = Math.abs(clientY - dragStartRef.current.y);

      if (
        deltaX > dragThresholdRef.current ||
        deltaY > dragThresholdRef.current
      ) {
        if (!isDragging) {
          setIsDragging(true);

          try {
            videoRefs.current.forEach((r) => {
              if (r?.current && !r.current.paused) r.current.pause();
            });
          } catch {}
        }
      }
    };

    const onUp = (e) => {
      dragStartRef.current = null;
      if (isDragging) {
        setTimeout(() => setIsDragging(false), 50);
      } else {
        setIsDragging(false);
      }
    };

    listEl.addEventListener("pointerdown", onDown, { passive: true });
    listEl.addEventListener("touchstart", onDown, { passive: true });
    listEl.addEventListener("mousedown", onDown, { passive: true });

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mousemove", onMove, { passive: true });

    window.addEventListener("pointerup", onUp, { passive: true });
    window.addEventListener("touchend", onUp, { passive: true });
    window.addEventListener("mouseup", onUp, { passive: true });
    listEl.addEventListener("mouseleave", onUp, { passive: true });

    return () => {
      try {
        listEl.removeEventListener("pointerdown", onDown);
        listEl.removeEventListener("touchstart", onDown);
        listEl.removeEventListener("mousedown", onDown);

        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("touchmove", onMove);
        window.removeEventListener("mousemove", onMove);

        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("touchend", onUp);
        window.removeEventListener("mouseup", onUp);
        listEl.removeEventListener("mouseleave", onUp);
      } catch {}
    };
  }, [isDragging]);

  function measureSlideWidth() {
    try {
      const listEl =
        sliderRef.current?.innerSlider?.list ??
        rootRef.current?.querySelector?.(".slick-list") ??
        null;
      if (listEl && listEl.clientWidth) {
        slideWidthRef.current = listEl.clientWidth;
        return;
      }

      const slideEl = rootRef.current?.querySelector?.(
        ".slick-slide:not(.slick-cloned)"
      );
      if (slideEl && slideEl.offsetWidth) {
        slideWidthRef.current = slideEl.offsetWidth;
        return;
      }

      slideWidthRef.current =
        rootRef.current?.clientWidth || window.innerWidth || 1;
    } catch {
      slideWidthRef.current = window.innerWidth || 1;
    }
  }

  useEffect(() => {
    return () => {
      videoRefs.current.forEach((r) => r?.current?.pause?.());
      document.body.style.overflow = "";
    };
  }, []);

  useEffect(() => {
    const listeners = [];
    videoRefs.current.forEach((r, idx) => {
      const el = r?.current;
      if (!el) return;
      const onLoaded = () => {
        try {
          const vertical = el.videoHeight > el.videoWidth;
          setIsVertical((prev) => {
            if (prev[idx] === vertical) return prev;
            const copy = [...prev];
            copy[idx] = vertical;
            return copy;
          });
        } catch {}
      };
      el.addEventListener("loadedmetadata", onLoaded);
      if (el.readyState >= 1) onLoaded();
      listeners.push({ el, onLoaded });
    });
    return () => {
      listeners.forEach(({ el, onLoaded }) =>
        el.removeEventListener("loadedmetadata", onLoaded)
      );
    };
  }, [thumbnails]);

  const handleTogglePlay = (index) => {
    if (isDragging) return;

    const refObj = videoRefs.current[index];
    if (!refObj || !refObj.current) return;
    const el = refObj.current;

    if (playingIndex === index) {
      el.pause();
      setPlayingIndex(null);
    } else {
      videoRefs.current.forEach((r, idx) => {
        if (r && r.current && idx !== index) r.current.pause();
      });
      el.play()
        .then(() => setPlayingIndex(index))
        .catch(() => {});
    }
  };

  const handleVideoPause = (index) => {
    setTimeout(() => {
      const ref = videoRefs.current[index];
      if (!ref?.current) return;

      if (isSeekingRef.current) return;

      if (ref.current.paused && playingIndex === index) {
        setPlayingIndex(null);
      }
    }, 50);
  };

  const handleVideoEnded = (index) => {
    setPlayingIndex(null);
  };

  const openImageOverlay = (src) => {
    setOverlayImageSrc(src);
    setOverlayOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeImageOverlay = () => {
    setOverlayOpen(false);
    setOverlayImageSrc(null);
    document.body.style.overflow = "";
  };

  const getSlideHeight = () => {
    if (width <= 420) return 340;
    if (width <= 760) return 420;
    if (width <= 1024) return 460;
    return 520;
  };
  const SLIDE_HEIGHT = getSlideHeight();

  const PRIMARY_COLOR = "#213360";
  const ACCENT_COLOR = "#2b4572";

  const mix = (aHex, bHex, t) => {
    try {
      return lerpColor(aHex, bHex, t);
    } catch {
      return aHex;
    }
  };

  const gradParams = [
    {
      angle: 135,
      whiteOpacity: 0.06,
      darkA: PRIMARY_COLOR,
      darkB: mix(PRIMARY_COLOR, ACCENT_COLOR, 0.22),
    },
    {
      angle: 155,
      whiteOpacity: 0.045,
      darkA: PRIMARY_COLOR,
      darkB: mix(PRIMARY_COLOR, ACCENT_COLOR, 0.45),
    },
    {
      angle: 120,
      whiteOpacity: 0.05,
      darkA: PRIMARY_COLOR,
      darkB: mix(PRIMARY_COLOR, ACCENT_COLOR, 0.72),
    },
  ];
  const positions = [
    { p1: "42% 34%", p2: "68% 68%", p3: "22% 58%" },
    { p1: "38% 28%", p2: "72% 72%", p3: "18% 62%" },
    { p1: "40% 30%", p2: "70% 70%", p3: "20% 60%" },
  ];

  const parseXY = (s) => {
    const parts = String(s)
      .split(/\s+/)
      .map((p) => parseFloat(p));
    return { x: parts[0] || 50, y: parts[1] || 50 };
  };

  const lerp = (a, b, t) => a + (b - a) * t;

  const hexToRgb = (hex) => {
    const h = hex.replace("#", "");
    if (h.length === 3) {
      return {
        r: parseInt(h[0] + h[0], 16),
        g: parseInt(h[1] + h[1], 16),
        b: parseInt(h[2] + h[2], 16),
      };
    }
    return {
      r: parseInt(h.substring(0, 2), 16),
      g: parseInt(h.substring(2, 4), 16),
      b: parseInt(h.substring(4, 6), 16),
    };
  };

  const lerpColor = (aHex, bHex, t) => {
    try {
      const a = hexToRgb(aHex);
      const b = hexToRgb(bHex);
      const r = Math.round(lerp(a.r, b.r, t));
      const g = Math.round(lerp(a.g, b.g, t));
      const bl = Math.round(lerp(a.b, b.b, t));
      return `rgb(${r}, ${g}, ${bl})`;
    } catch {
      return aHex;
    }
  };

  useEffect(() => {
    let rafId = null;
    let mounted = true;
    const lastFrameRef = { current: 0 };

    const findTrack = () => {
      if (!rootRef.current) return null;
      if (sliderRef.current?.innerSlider?.list) {
        const tr =
          sliderRef.current.innerSlider.list.querySelector(".slick-track");
        if (tr) return tr;
      }
      const tr = rootRef.current.querySelector(".slick-track");
      return tr || null;
    };

    measureSlideWidth();

    const alpha = 0.18;

    const tick = () => {
      if (!mounted) return;

      const now = performance.now();
      if (now - lastFrameRef.current < 33) {
        rafId = window.requestAnimationFrame(tick);
        return;
      }
      lastFrameRef.current = now;

      const tr = trackRef.current || findTrack();
      if (tr) trackRef.current = tr;

      if (trackRef.current) {
        const inlineT = trackRef.current.style.transform;
        let tx = 0;
        if (inlineT && inlineT !== "none") {
          const m = inlineT.match(/matrix3?d?\((.+)\)/);
          if (m && m[1]) {
            const vals = m[1].split(",").map((v) => parseFloat(v));
            if (vals.length >= 6) {
              tx = vals[4];
            } else if (vals.length === 16) {
              tx = vals[12];
            }
          } else {
            const txMatch = inlineT.match(/(-?\d+(\.\d+)?)px/);
            if (txMatch && txMatch[1]) tx = parseFloat(txMatch[1]);
          }
        } else {
          const attr = trackRef.current.getAttribute("style") || "";
          const m = attr.match(/transform:\s*matrix.*\((.+)\)/);
          if (m && m[1]) {
            const vals = m[1].split(",").map((v) => parseFloat(v));
            if (vals.length >= 6) tx = vals[4];
          }
        }

        const slideW = slideWidthRef.current || window.innerWidth || 1;
        const rawIndex = -tx / slideW;
        const maxIndex = slides.length - 1;
        const clampedRaw = Math.max(0, Math.min(rawIndex, maxIndex));
        const base = Math.floor(clampedRaw);
        const next = Math.min(base + 1, maxIndex);
        const progress = Math.max(0, Math.min(1, clampedRaw - base));

        const a = gradParams[base];
        const b = gradParams[next];

        const angle = lerp(a.angle, b.angle, progress);
        const whiteOpacity = lerp(a.whiteOpacity, b.whiteOpacity, progress);
        const darkB = lerpColor(a.darkB, b.darkB, progress);

        const pa = positions[base];
        const pb = positions[next];
        const pa1 = parseXY(pa.p1),
          pb1 = parseXY(pb.p1);
        const p1x = lerp(pa1.x, pb1.x, progress);
        const p1y = lerp(pa1.y, pb1.y, progress);

        const pa2 = parseXY(pa.p2),
          pb2 = parseXY(pb.p2);
        const p2x = lerp(pa2.x, pb2.x, progress);
        const p2y = lerp(pa2.y, pb2.y, progress);

        const pa3 = parseXY(pa.p3),
          pb3 = parseXY(pb.p3);
        const p3x = lerp(pa3.x, pb3.x, progress);
        const p3y = lerp(pa3.y, pb3.y, progress);

        const s = smoothedRef.current;
        if (s.angle === null) {
          s.angle = angle;
          s.whiteOpacity = whiteOpacity;
          s.darkBColor = darkB;
          s.p1x = p1x;
          s.p1y = p1y;
          s.p2x = p2x;
          s.p2y = p2y;
          s.p3x = p3x;
          s.p3y = p3y;
        } else {
          s.angle = s.angle + (angle - s.angle) * alpha;
          s.whiteOpacity =
            s.whiteOpacity + (whiteOpacity - s.whiteOpacity) * alpha;
          try {
            const parseRGB = (str) => {
              const m = String(str).match(
                /rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/
              );
              return m
                ? [parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10)]
                : null;
            };
            const prevRGB = parseRGB(s.darkBColor) ||
              parseRGB(darkB) || [0, 0, 0];
            const newRGB = parseRGB(darkB) || prevRGB;
            const sr = Math.round(lerp(prevRGB[0], newRGB[0], alpha));
            const sg = Math.round(lerp(prevRGB[1], newRGB[1], alpha));
            const sb = Math.round(lerp(prevRGB[2], newRGB[2], alpha));
            s.darkBColor = `rgb(${sr}, ${sg}, ${sb})`;
          } catch {
            s.darkBColor = darkB;
          }
          s.p1x = s.p1x + (p1x - s.p1x) * alpha;
          s.p1y = s.p1y + (p1y - s.p1y) * alpha;
          s.p2x = s.p2x + (p2x - s.p2x) * alpha;
          s.p2y = s.p2y + (p2y - s.p2y) * alpha;
          s.p3x = s.p3x + (p3x - s.p3x) * alpha;
          s.p3y = s.p3y + (p3y - s.p3y) * alpha;
        }

        if (rootRef.current) {
          // if (!isAlternate) {
          const BITE_LEAN = 0.35;
          const BITE_V_LEAN = 0.18;
          const biteX = s.p1x + (s.p3x - s.p1x) * BITE_LEAN;
          const biteY = s.p1y + (s.p3y - s.p1y) * BITE_V_LEAN;

          const biteRadial = `radial-gradient(circle at ${biteX}% ${biteY}%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.06) 28%, transparent 60%)`;
          const bgLinear = `linear-gradient(${s.angle}deg, ${a.darkA} 0%, ${s.darkBColor} 60%, rgba(255,255,255,${s.whiteOpacity}) 100%)`;

          rootRef.current.style.background = `${biteRadial}, ${bgLinear}`;
          // } else {
          //   const grayA = "#f7f8fa";
          //   const grayB = "#e6e8eb";
          //   const grayC = "#d5d8db";

          //   const BITE_LEAN = 0.36;
          //   const BITE_V_LEAN = 0.16;
          //   const biteX = s.p1x + (s.p3x - s.p1x) * BITE_LEAN;
          //   const biteY = s.p1y + (s.p3y - s.p1y) * BITE_V_LEAN;

          //   const biteRadialAlt = `radial-gradient(circle at ${biteX}% ${biteY}%, rgba(255,255,255,0.95) 0%, rgba(250,250,250,0.85) 20%, rgba(238,238,238,0.5) 50%, transparent 70%)`;

          //   const linearAlt = `linear-gradient(${s.angle}deg, ${grayA} 0%, ${grayB} 55%, ${grayC} 100%)`;

          //   rootRef.current.style.background = `${biteRadialAlt}, ${linearAlt}`;
          // }
        }

        if (radial1Ref.current) {
          radial1Ref.current.style.left = `${s.p1x}%`;
          radial1Ref.current.style.top = `${s.p1y}%`;
        }
        if (radial2Ref.current) {
          radial2Ref.current.style.left = `${s.p2x}%`;
          radial2Ref.current.style.top = `${s.p2y}%`;
        }
        if (radial3Ref.current) {
          radial3Ref.current.style.left = `${s.p3x}%`;
          radial3Ref.current.style.top = `${s.p3y}%`;
        }
        if (radial4Ref.current) {
          const biteX = s.p1x + (s.p3x - s.p1x) * 0.35;
          const biteY = s.p1y + (s.p3y - s.p1y) * 0.18;
          radial4Ref.current.style.left = `${biteX}%`;
          radial4Ref.current.style.top = `${biteY}%`;
          const dynamicOpacity = Math.max(
            0.6,
            Math.min(0.98, (s.whiteOpacity || 0.06) * 12)
          );
          radial4Ref.current.style.opacity = `${dynamicOpacity}`;
        }
      }

      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);
    return () => {
      mounted = false;
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [thumbnails, width]);

  useEffect(() => {
    try {
      const init = gradParams[slideIndex % gradParams.length];
      const initPos = positions[slideIndex % positions.length];
      const p1 = parseXY(initPos.p1);
      const p2 = parseXY(initPos.p2);
      const p3 = parseXY(initPos.p3);

      if (rootRef.current) {
        // if (!isAlternate) {
        const initBiteX = p1.x + (p3.x - p1.x) * 0.35;
        const initBiteY = p1.y + (p3.y - p1.y) * 0.18;
        const biteRadial = `radial-gradient(circle at ${initBiteX}% ${initBiteY}%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.06) 28%, transparent 60%)`;
        const linear = `linear-gradient(${init.angle}deg, ${init.darkA} 0%, ${init.darkB} 60%, rgba(255,255,255,${init.whiteOpacity}) 100%)`;
        rootRef.current.style.background = `${biteRadial}, ${linear}`;
        // } else {
        //   const initBiteX = p1.x + (p3.x - p1.x) * 0.36;
        //   const initBiteY = p1.y + (p3.y - p1.y) * 0.16;
        //   const biteRadialAlt = `radial-gradient(circle at ${initBiteX}% ${initBiteY}%, rgba(255,255,255,0.95) 0%, rgba(250,250,250,0.85) 20%, rgba(238,238,238,0.5) 50%, transparent 70%)`;
        //   const linearAlt = `linear-gradient(${init.angle}deg, #f7f8fa 0%, #e6e8eb 55%, #d5d8db 100%)`;
        //   rootRef.current.style.background = `${biteRadialAlt}, ${linearAlt}`;
        // }
      }
      if (radial1Ref.current) {
        radial1Ref.current.style.left = `${p1.x}%`;
        radial1Ref.current.style.top = `${p1.y}%`;
      }
      if (radial2Ref.current) {
        radial2Ref.current.style.left = `${p2.x}%`;
        radial2Ref.current.style.top = `${p2.y}%`;
      }
      if (radial3Ref.current) {
        radial3Ref.current.style.left = `${p3.x}%`;
        radial3Ref.current.style.top = `${p3.y}%`;
      }
      if (radial4Ref.current) {
        const initBiteX = p1.x + (p3.x - p1.x) * 0.35;
        const initBiteY = p1.y + (p3.y - p1.y) * 0.18;
        radial4Ref.current.style.left = `${initBiteX}%`;
        radial4Ref.current.style.top = `${initBiteY}%`;
      }
    } catch {}
  }, []);

  const sliderSettings = {
    dots: true,
    arrows: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: false,
    beforeChange: (oldIndex, newIndex) => {
      if (playingIndex !== null) {
        const ref = videoRefs.current[playingIndex];
        if (ref?.current) ref.current.pause();
        setPlayingIndex(null);
      }
    },
    afterChange: (i) => {
      setSlideIndex(i);
      setTimeout(measureSlideWidth, 60);
    },
    prevArrow: <Arrow direction="left" color={"white"} />,
    nextArrow: <Arrow direction="right" color={"white"} />,
  };

  const posterForIndex = (idx) => {
    if (idx === 1) return thumbnails[0];
    if (idx === 2) return thumbnails[1];
    return undefined;
  };

  const hospitalContactUrl =
    mediaUrls?.hospitalContactUrl || "#contact-hospitals";
  const isMobile = width <= 880;

  const radial1Bg =
    "radial-gradient(circle, rgba(255,255,255,0.26) 0%, transparent 50%)";
  const radial2Bg =
    "radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 50%)";
  const radial3Bg =
    "radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 50%)";
  const radial4Bg =
    "radial-gradient(circle, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0.06) 30%, transparent 70%)";

  return (
    <section className="reviews-section section-gap">
      <section className="bg-color-grey polygon-pattern-2 section-gap">
        <EyeDonationHeader
          mediaUrls={mediaUrls}
          isMobile={isMobile}
          // isAlternate={isAlternate}
        />

        <div
          ref={rootRef}
          style={{
            // paddingBottom: 28,
            // paddingTop: 0,
            boxShadow: "0 18px 45px rgba(33,51,96,0.08)",
            color: "#ffffff",
            position: "relative",
            overflow: "hidden",
            paddingTop: !isMobile && 100,
            paddingBottom: !isMobile && 100,
            paddingRight: "40px",
            paddingLeft: "40px",
          }}
        >
          {/* Radial accent layers (styles depend on theme) */}
          <div
            ref={radial1Ref}
            style={{
              position: "absolute",
              left: "42%",
              top: "58%",
              width: 320,
              height: 320,
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              background: radial1Bg,
              opacity: 0.95,
              zIndex: 0,
              willChange: "left, top, opacity",
              mixBlendMode: "screen",
            }}
          />
          <div
            ref={radial2Ref}
            style={{
              position: "absolute",
              left: "70%",
              top: "70%",
              width: 280,
              height: 280,
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              background: radial2Bg,
              opacity: 0.9,
              zIndex: 0,
              willChange: "left, top, opacity",
              mixBlendMode: "screen",
            }}
          />
          <div
            ref={radial3Ref}
            style={{
              position: "absolute",
              left: "20%",
              top: "60%",
              width: 260,
              height: 260,
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              background: radial3Bg,
              opacity: 0.85,
              zIndex: 0,
              willChange: "left, top, opacity",
              mixBlendMode: "screen",
            }}
          />
          <div
            ref={radial4Ref}
            style={{
              position: "absolute",
              left: "34%",
              top: "42%",
              width: 380,
              height: 380,
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
              background: radial4Bg,
              opacity: 0.92,
              zIndex: 1,
              willChange: "left, top, opacity, transform",
              mixBlendMode: "screen",
            }}
          />

          {/* SLIDER */}
          <div style={{ paddingTop: 8, position: "relative", zIndex: 2 }}>
            <Slider ref={sliderRef} {...sliderSettings}>
              {slides.map((s, idx) => {
                const gridStyle = isMobile
                  ? {
                      display: "grid",
                      gridTemplateColumns: "1fr",
                      gap: 12,
                      alignItems: "start",
                      gridTemplateAreas: `"media" "content"`,
                      width: "100%",
                    }
                  : {
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 20,
                      alignItems: "center",
                      height: "100%",
                      gridTemplateAreas:
                        s.layout === "media-left"
                          ? '"media content"'
                          : '"content media"',
                    };

                const posterUrl = posterForIndex(idx);

                const mediaContainerStyle = isMobile
                  ? {
                      gridArea: "media",
                      position: "relative",
                      width: "100%",
                      maxWidth: "100%",
                      height: SLIDE_HEIGHT,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }
                  : s.type === "video" && isVertical[idx]
                  ? {
                      position: "relative",
                      width: "100%",
                      maxWidth: 420,
                      display: "flex",
                      alignItems: idx === 2 ? "center" : "flex-start",
                      justifyContent: "center",
                      height: idx === 2 ? SLIDE_HEIGHT : "auto",
                    }
                  : {
                      position: "relative",
                      width: "100%",
                      maxWidth: 520,
                      display: "flex",
                      alignItems: idx === 2 ? "center" : "flex-start",
                      justifyContent: "center",
                      height: idx === 2 ? SLIDE_HEIGHT : "auto",
                    };

                const verticalVideoStyle =
                  s.type === "video" && isVertical[idx]
                    ? {
                        display: "block",
                        width: "auto",
                        maxWidth: "100%",
                        maxHeight: isMobile
                          ? SLIDE_HEIGHT - 20
                          : SLIDE_HEIGHT - 20,
                        objectFit: "contain",
                        background: "#000",
                        borderRadius: "6px",
                        willChange: "transform",
                        contain: "layout",
                        pointerEvents: isInteracting ? "none" : "auto",
                      }
                    : {
                        display: "block",
                        width: "100%",
                        height: isMobile
                          ? Math.max(180, Math.min(360, SLIDE_HEIGHT - 20))
                          : Math.max(180, Math.min(360, SLIDE_HEIGHT - 120)),
                        objectFit: "cover",
                        background: !isMobile && "#000",
                        borderRadius: "6px",
                        willChange: "transform",
                        contain: "layout",
                        pointerEvents: isInteracting ? "none" : "auto",
                        padding: isMobile && 12,
                      };

                const slideWrapperStyle = {
                  padding: "18px 8px",
                  height: isMobile ? "auto" : SLIDE_HEIGHT,
                  boxSizing: "border-box",
                };

                return (
                  <div key={s.key} style={slideWrapperStyle}>
                    <div style={gridStyle}>
                      {/* MEDIA */}
                      <div
                        style={{
                          gridArea: "media",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {s.type === "image" ? (
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => openImageOverlay(s.src)}
                            onKeyDown={(e) =>
                              e.key === "Enter" && openImageOverlay(s.src)
                            }
                            style={{
                              width: "100%",
                              maxWidth: isMobile ? "100%" : 520,
                              height: "100%",
                              borderRadius: 14,
                              overflow: "hidden",
                              cursor: "pointer",
                              boxShadow: "0 12px 36px rgba(0,0,0,0.35)",
                              background: !isMobile
                                ? "rgba(0,0,0,0.02)"
                                : "rgba(255,255,255,0.04)",
                              display: "flex",
                              alignItems: "stretch",
                              padding: isMobile ? 14 : 0,
                            }}
                          >
                            <img
                              src={s.src}
                              alt={s.title}
                              style={{
                                display: "block",
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </div>
                        ) : (
                          <div style={mediaContainerStyle}>
                            <video
                              ref={videoRefs.current[idx]}
                              src={s.src}
                              controls={playingIndex === idx}
                              poster={posterUrl}
                              preload="metadata"
                              playsInline
                              onPlay={() => setPlayingIndex(idx)}
                              onPause={() => handleVideoPause(idx)}
                              onSeeking={() => {
                                isSeekingRef.current = true;
                              }}
                              onSeeked={() => {
                                isSeekingRef.current = false;
                                const ref = videoRefs.current[idx];
                                if (ref?.current && !ref.current.paused) {
                                  setPlayingIndex(idx);
                                }
                              }}
                              onEnded={() => handleVideoEnded(idx)}
                              style={verticalVideoStyle}
                            />

                            {playingIndex !== idx && (
                              <div
                                onClick={() => handleTogglePlay(idx)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) =>
                                  e.key === "Enter" && handleTogglePlay(idx)
                                }
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  zIndex: 2,
                                  cursor: isInteracting
                                    ? "grabbing"
                                    : "pointer",
                                  pointerEvents: isInteracting
                                    ? "none"
                                    : "auto",
                                  userSelect: "none",
                                }}
                              >
                                <div
                                  style={{
                                    width: 84,
                                    height: 84,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "rgba(0,0,0,0.48)",
                                    boxShadow: "0 12px 36px rgba(0,0,0,0.5)",
                                  }}
                                >
                                  <FaPlay
                                    style={{ color: "white", fontSize: 30 }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* CONTENT */}
                      <div
                        style={{
                          gridArea: "content",
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          padding: 18,
                          boxSizing: "border-box",
                        }}
                      >
                        <div
                          style={{
                            padding: 18,
                            borderRadius: 12,
                            background: "transparent",
                            color: "white",
                            maxWidth: 720,
                            textShadow: "0 6px 20px rgba(0,0,0,0.45)",
                            width: "100%",
                          }}
                        >
                          <h3
                            style={{
                              margin: "0 0 10px",
                              fontSize: isMobile ? 24 : 35,
                              color: "white",
                              fontWeight: 700,
                            }}
                          >
                            {s.heading}
                          </h3>
                          <p
                            style={{
                              margin: "0 0 12px",
                              color: "rgba(255,255,255,0.92)",
                              lineHeight: 1.6,
                              fontSize: isMobile ? 15 : 25,
                            }}
                          >
                            {s.text}
                          </p>

                          {/* bullets with appropriate colors */}
                          {/* Conditional bullet points - skip for first slide */}
                          {idx !== 0 && Array.isArray(s.bullets) ? (
                            <ul
                              style={{
                                margin: isMobile
                                  ? "0 0 12px 18px"
                                  : "0 0 18px 20px",
                                color: "rgba(255,255,255,0.95)",
                                lineHeight: 1.6,
                                listStyleType: "circle",
                                fontSize: isMobile ? 14 : 23,
                              }}
                            >
                              {s.bullets.map((b, i) => (
                                <li key={i} style={{ marginBottom: 6 }}>
                                  {b}
                                </li>
                              ))}
                            </ul>
                          ) : idx !== 0 ? (
                            <ul
                              style={{
                                margin: "0 0 18px 20px",
                                color: "rgba(255,255,255,0.9)",
                                listStyleType: "circle",
                              }}
                            >
                              <li>
                                {t("home.eyeDonationSection.contactHospitals")}
                              </li>
                              <li>
                                {t(
                                  "home.eyeDonationSection.donorRegistrationSteps"
                                )}
                              </li>
                              <li>
                                {t(
                                  "home.eyeDonationSection.timelyCoordination"
                                )}
                              </li>
                            </ul>
                          ) : null}

                          {/* first-slide bottom line CTA */}
                          {s.bottomLine && (
                            <div style={{ marginTop: 6, opacity: 0.95 }}>
                              <span
                                style={{
                                  color: "rgba(255,255,255,0.95)",
                                  fontWeight: 600,
                                  fontSize: isMobile ? 15 : 25,
                                  marginTop: "34px",
                                }}
                              >
                                {s.bottomLine}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Slider>
          </div>

          {/* IMAGE OVERLAY */}
          {overlayOpen && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.8)",
                zIndex: 9999,
              }}
              onClick={closeImageOverlay}
            >
              <div
                style={{
                  position: "relative",
                  maxWidth: "96%",
                  maxHeight: "96%",
                  padding: 10,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={overlayImageSrc}
                  alt="large"
                  style={{ width: "100%", height: "auto", borderRadius: 10 }}
                />
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    marginTop: 12,
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    style={{
                      background: "transparent",
                      color: "rgba(255,255,255,0.95)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      padding: "10px 14px",
                      borderRadius: 8,
                      cursor: "pointer",
                      fontWeight: 700,
                    }}
                    onClick={closeImageOverlay}
                    aria-label={t("home.eyeDonationSection.closeOverlay")}
                  >
                    {t("home.eyeDonationSection.closeButton")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ backgroundColor: "#f2f4f7", height: "120px" }} />

        {/* Floating theme toggle bottom-left */}
      </section>
    </section>
  );
};

export default EyeDonationSlider;
